import { TypedDocumentNode, gql } from '@apollo/client';
import { faSignalStream } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { isFuture } from 'date-fns';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { ClickInstantBuy_Flow } from '@sorare/core/src/protos/events/so5/shared/events';

import { useGetAuctionDetails } from 'hooks/offers/useGetAuctionDetails';

import { TokenImg } from '../Token/TokenImg';
import { Actions } from './Actions';
import { CornerBadges } from './CornerBadges';
import { Details } from './Details';
import { MarketCreditCornerBadge } from './MarketCreditCornerBadge';
import type { PlayerDrawerToken_anyCard } from './__generated__/index.graphql';

const Wrapper = styled.div`
  position: relative;
  isolation: isolate;
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  flex-shrink: 0;
  &.auction {
    background-color: var(--c-nd-50);
    border: none;
  }
`;

const ImgWrapper = styled.div`
  width: calc(5 * var(--unit));
  flex-shrink: 0;
`;

const AuctionHeader = styled(Horizontal)`
  padding: var(--half-unit);
  border-radius: var(--double-unit) var(--double-unit) 0 0;
  background-color: rgba(var(--c-rgb-red-300), 0.16);
`;

const Relative = styled.div`
  position: relative;
  isolation: isolate;
`;

const Content = styled(Vertical)`
  padding: var(--double-unit);
`;

const PositionedCornerBadges = styled(Vertical).attrs({ gap: 0.5 })`
  align-items: flex-end;
  position: absolute;
  top: var(--unit);
  right: -1px;
  z-index: 1;
`;

type Props = {
  anyCard: PlayerDrawerToken_anyCard;
  bestValue?: boolean;
  bestPrice?: boolean;
  highestLevel?: boolean;
  canUseConversionCredit?: boolean;
  clickInstantBuyFlow?: ClickInstantBuy_Flow;
};

export const PlayerDrawerToken = ({
  anyCard,
  bestValue,
  bestPrice = false,
  highestLevel = false,
  canUseConversionCredit = false,
  clickInstantBuyFlow,
}: Props) => {
  const auction = useGetAuctionDetails(anyCard.latestEnglishAuction);

  const isBundledAuction = !!(
    anyCard.latestEnglishAuction &&
    isFuture(anyCard.latestEnglishAuction.endDate) &&
    anyCard.latestEnglishAuction?.cards?.length > 1
  );

  return (
    <Wrapper className={classNames({ auction })}>
      {auction && (
        <AuctionHeader center>
          <FontAwesomeIcon icon={faSignalStream} color="var(--c-red-600)" />
          <LabelS color="var(--c-red-600)" bold>
            <TimeLeft time={auction.endDate} />
          </LabelS>
        </AuctionHeader>
      )}
      <Relative>
        <PositionedCornerBadges>
          <CornerBadges
            bestValue={bestValue}
            bestPrice={bestPrice}
            highestLevel={highestLevel}
            marketCredit={
              <MarketCreditCornerBadge
                anyCard={anyCard}
                canUseConversionCredit={canUseConversionCredit}
              />
            }
          />
        </PositionedCornerBadges>
        <Content gap={2}>
          <Horizontal gap={2}>
            <ImgWrapper>
              <TokenImg
                card={anyCard}
                isBundledAuction={isBundledAuction}
                isDesktopLayout={false}
              />
            </ImgWrapper>
            <Details anyCard={anyCard} />
          </Horizontal>
          <Actions
            anyCard={anyCard}
            clickInstantBuyFlow={clickInstantBuyFlow}
          />
        </Content>
      </Relative>
    </Wrapper>
  );
};

PlayerDrawerToken.fragments = {
  anyCard: gql`
    fragment PlayerDrawerToken_anyCard on AnyCardInterface {
      slug
      latestEnglishAuction {
        id
        endDate
        cards: anyCards {
          slug
        }
        ...useGetAuctionDetails_auction
      }
      ...TokenImg_anyCard
      ...PlayerDrawerTokenDetails_anyCard
      ...PlayerDrawerTokenActions_anyCard
      ...useIsPrimaryBuyLive_anyCard
      ...MarketCreditCornerBadge_anyCard
    }
    ${TokenImg.fragments.anyCard}
    ${Details.fragments.anyCard}
    ${Actions.fragments.anyCard}
    ${useGetAuctionDetails.fragments.auction}
    ${MarketCreditCornerBadge.fragments.anyCard}
  ` as TypedDocumentNode<PlayerDrawerToken_anyCard>,
};
